import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useToastStore } from "@/store/toastStore";
import {
  Box,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  Phone,
  Email,
  LocationOn,
  Visibility,
  UploadFile,
  Download,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CustomerTypeBadge,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
} from "@/components/ui";
import {
  customerService,
  CustomerType,
} from "@/services/customer.service";
import { zoneService } from "@/services/zone.service";
import { formatCustomerName } from "@/utils/formatCustomerName";

/** Template CSV pour l'import clients (format Excel : colonnes en français) */
const CUSTOMERS_CSV_TEMPLATE = [
  "nom\tAdresse\tNuméro\tRéduction\tRécurrence\tLivraison\tCommentaire\tMode paiement\tIndice\tAdresse complète\tLivreur\tOrdre\tAdresse de facturation",
  "ALEXANDER Florentina\tmajestic\t06 59 22 72 34\t14,70 €\t\t\t\tEn compte\t366\t\"ALEXANDER Florentina  majestic\n06 59 22 72 34\"\t17 - Tribunaux\t2\t\"ALEXANDER Florentina  TGI Larivot\n06.59.22.72.34\"",
  "BARBOSA DOS SANTOS Naiara\tCour d'appel\t06 94 49 05 57\t14,60 €\t\t\t\tEn compte\t12,8\t\"BARBOSA DOS SANTOS Naiara Cour d'appel\n06 94 49 05 57\"\t19 - CA\t\t\"BARBOSA-DOS SANTOS Naiara\n97300 CAYENNE\"",
].join("\n");

function downloadCustomersCsvTemplate(): void {
  const blob = new Blob(["\uFEFF" + CUSTOMERS_CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "template-import-clients.csv";
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Page de gestion des clients
 * Liste avec recherche, filtres et actions CRUD
 */
export function CustomersPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = usePersistedState("customers.searchQuery", "");
  const [typeFilter, setTypeFilter] = usePersistedState<CustomerType | "all">("customers.typeFilter", "all");
  const [zoneFilter, setZoneFilter] = usePersistedState("customers.zoneFilter", "all");
  const [statusFilter, setStatusFilter] = usePersistedState<
    "all" | "active" | "inactive"
  >("customers.statusFilter", "all");

  // État pour le dialog de confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    customerId: string | null;
    customerName: string;
  }>({
    open: false,
    customerId: null,
    customerName: "",
  });

  // État pour le dialog d'import CSV
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{
    created: number;
    errors: { row: number; message: string }[];
  } | null>(null);

  // Récupérer les clients
  const {
    data: customers = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["customers"],
    queryFn: () => customerService.getAll(),
  });

  // Récupérer les zones pour le filtre
  const { data: zones = [] } = useQuery({
    queryKey: ["zones"],
    queryFn: () => zoneService.getAll(),
  });

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour supprimer un client
  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      success("Client supprimé avec succès");
      setDeleteConfirm({ open: false, customerId: null, customerName: "" });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la suppression du client",
      );
    },
  });

  // Mutation pour import CSV
  const importCsvMutation = useMutation({
    mutationFn: (csvContent: string) => customerService.importCsv(csvContent),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setImportResult(data);
      if (data.errors.length === 0) {
        success(`${data.created} client(s) importé(s) avec succès`);
      } else if (data.created > 0) {
        success(`${data.created} client(s) créé(s), ${data.errors.length} erreur(s) (voir détail).`);
      }
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message || "Erreur lors de l'import CSV.",
      );
    },
  });

  // Filtrer les clients
  const filteredCustomers = customers.filter((customer) => {
    const q = searchQuery.toLowerCase();
    // Recherche par nom, prénom, email, téléphone
    const matchesSearch =
      !searchQuery ||
      formatCustomerName(customer).toLowerCase().includes(q) ||
      customer.firstName?.toLowerCase().includes(q) ||
      customer.lastName?.toLowerCase().includes(q) ||
      customer.email?.toLowerCase().includes(q) ||
      customer.phone?.includes(searchQuery);

    // Filtre par type
    const matchesType = typeFilter === "all" || customer.type === typeFilter;

    // Filtre par zone (au niveau des adresses)
    const matchesZone =
      zoneFilter === "all" ||
      (customer.addresses?.some((a) => a.zoneId === zoneFilter) ?? false);

    // Filtre par statut
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && customer.isActive) ||
      (statusFilter === "inactive" && !customer.isActive);

    return matchesSearch && matchesType && matchesZone && matchesStatus;
  });

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteConfirm({ open: true, customerId: id, customerName: name });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.customerId) {
      deleteMutation.mutate(deleteConfirm.customerId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, customerId: null, customerName: "" });
  };

  if (isLoading) {
    return <LoadingSpinner message="Chargement des clients..." />;
  }

  if (queryError) {
    return <ErrorMessage message="Erreur lors du chargement des clients." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Clients"
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="secondary"
              startIcon={<UploadFile />}
              onClick={() => {
                setImportResult(null);
                setImportFile(null);
                setImportDialogOpen(true);
              }}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              startIcon={<Add />}
              onClick={() => navigate("/customers/new")}
            >
              Nouveau Client
            </Button>
          </Box>
        }
      />

      {/* Dialog Import CSV */}
      <Dialog
        open={importDialogOpen}
        onClose={() => {
          if (!importCsvMutation.isPending) {
            setImportDialogOpen(false);
            setImportFile(null);
            setImportResult(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Importer des clients (CSV)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Colonnes acceptées : <strong>nom</strong>, Adresse, Numéro, Réduction, Récurrence, Livraison, Commentaire, Mode paiement, <strong>Indice</strong> (ou Livreur, ex. &quot;17 - Tribunaux&quot;), Adresse complète, Ordre, Adresse de facturation. Le <strong>nom</strong> et une zone (<strong>Indice</strong> ou <strong>Livreur</strong>) sont requis. Séparateur : tabulation, point-virgule ou virgule. Champs entre guillemets autorisés.
          </Typography>
          {!importResult ? (
            <>
              <MuiButton
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadCustomersCsvTemplate}
                sx={{ mb: 2 }}
              >
                Télécharger le template
              </MuiButton>
              <input
                accept=".csv"
                style={{ display: "none" }}
                id="import-csv-file"
                type="file"
                onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
              />
              <label htmlFor="import-csv-file">
                <MuiButton
                  variant="outlined"
                  component="span"
                  startIcon={<UploadFile />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {importFile ? importFile.name : "Choisir un fichier CSV"}
                </MuiButton>
              </label>
            </>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{importResult.created}</strong> client(s) créé(s).
                {importResult.errors.length > 0 && (
                  <> {importResult.errors.length} erreur(s) (lignes ignorées).</>
                )}
              </Typography>
              {importResult.errors.length > 0 && (
                <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                  {importResult.errors.map((e, i) => (
                    <ListItem key={i}>
                      <ListItemText
                        primary={`Ligne ${e.row}: ${e.message}`}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          {importCsvMutation.isPending && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          {importResult ? (
            <MuiButton
              onClick={() => {
                setImportDialogOpen(false);
                setImportFile(null);
                setImportResult(null);
              }}
            >
              Fermer
            </MuiButton>
          ) : (
            <>
              <MuiButton
                onClick={() => {
                  setImportDialogOpen(false);
                  setImportFile(null);
                }}
              >
                Annuler
              </MuiButton>
              <MuiButton
                variant="contained"
                disabled={!importFile || importCsvMutation.isPending}
                onClick={() => {
                  if (!importFile) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const text = reader.result as string;
                    importCsvMutation.mutate(text);
                  };
                  reader.readAsText(importFile, "UTF-8");
                }}
              >
                Importer
              </MuiButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      <FiltersCard>
        <InputField
            placeholder="Rechercher un client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            icon={<Search />}
            iconPosition="start"
            sx={{ flexGrow: 1, minWidth: 250, "& .MuiOutlinedInput-root": { height: 40 } }}
          />

          {/* Filtre par type */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) =>
                setTypeFilter(e.target.value as CustomerType | "all")
              }
              label="Type"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="INSTITUTION">Institution</MenuItem>
              <MenuItem value="COMPANY">Entreprise</MenuItem>
              <MenuItem value="INDIVIDUAL">Particulier</MenuItem>
            </Select>
          </FormControl>

          {/* Filtre par zone */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Zone</InputLabel>
            <Select
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              label="Zone"
            >
              <MenuItem value="all">Toutes</MenuItem>
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  {zone.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filtre par statut */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "all" | "active" | "inactive")
              }
              label="Statut"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="active">Actifs</MenuItem>
              <MenuItem value="inactive">Inactifs</MenuItem>
            </Select>
          </FormControl>
      </FiltersCard>

      {/* Tableau des clients */}
      {filteredCustomers.length === 0 ? (
        <EmptyState
          title="Aucun client trouvé"
          description={
            searchQuery ||
            typeFilter !== "all" ||
            zoneFilter !== "all" ||
            statusFilter !== "all"
              ? "Aucun client ne correspond aux critères de recherche."
              : "Il n'y a aucun client pour le moment. Créez-en un nouveau !"
          }
          action={
            !searchQuery &&
            typeFilter === "all" &&
            zoneFilter === "all" &&
            statusFilter === "all"
              ? {
                  label: "Créer un client",
                  onClick: () => navigate("/customers/new"),
                }
              : undefined
          }
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Zone</TableCell>
                  <TableCell>Téléphone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCustomerName(customer)}
                        </Typography>
                      </Box>
                      {customer.contactPerson && (
                        <Typography variant="caption" color="text.secondary">
                          Contact: {customer.contactPerson}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <CustomerTypeBadge type={customer.type} />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <LocationOn
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        <Typography variant="body2">
                          {(customer.addresses?.find((a) => a.isDefault) ?? customer.addresses?.[0])?.zone?.name ?? "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {customer.phone ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Phone
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {customer.phone}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.email ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Email
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {customer.email}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={customer.isActive ? "Actif" : "Inactif"}
                        color={customer.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        aria-label="Voir la fiche client"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/customers/${customer.id}/edit`)
                        }
                        aria-label="Modifier"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeleteClick(customer.id, formatCustomerName(customer))
                        }
                        aria-label="Supprimer"
                        color="error"
                        disabled={deleteMutation.isPending}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Compteur de résultats */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              {filteredCustomers.length} client
              {filteredCustomers.length > 1 ? "s" : ""} trouvé
              {filteredCustomers.length !== customers.length
                ? ` sur ${customers.length}`
                : ""}
            </Typography>
          </Box>
        </Card>
      )}

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Supprimer le client"
        message={`Êtes-vous sûr de vouloir supprimer le client "${deleteConfirm.customerName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
